/**
 * Remembers the deep link a user landed on (path + query + hash) so it can be
 * restored after the auth bootstrap, which otherwise bounces every direct URL
 * through `/` and would drop the query string (e.g. Plan Index filters).
 *
 * The in-memory snapshot is authoritative for the current page load; the
 * sessionStorage copy is what survives the external Auth0 redirect round-trip
 * (React Router location state is lost on that navigation). All storage access
 * is guarded because it throws in private-mode / disabled-storage browsers.
 *
 * Consumption is single-shot: the value is cleared only after a confirmed
 * post-login navigation, never at read time.
 */

const KEY = 'v1:postLoginRedirect';

let inMemory: string | null = null;
let consumed = false;

/**
 * Records the current location as the post-login redirect target. Intended to
 * run once at app bootstrap, before any router navigation can mutate the URL.
 */
export function capturePostLoginRedirect(): void {
    try {
        const { pathname, search, hash } = window.location;
        if (pathname === '/' || pathname.includes('callback')) return;
        const deepLink = `${pathname}${search}${hash}`;
        inMemory = deepLink;
        try {
            sessionStorage.setItem(KEY, deepLink);
        } catch {
            /* private mode / quota / disabled - in-memory snapshot still applies */
        }
    } catch {
        /* window.location unavailable - nothing to capture */
    }
}

/** Returns the captured deep link, or null once consumed / never captured. */
export function getPostLoginRedirect(): string | null {
    if (consumed) return null;
    if (inMemory) return inMemory;
    try {
        return sessionStorage.getItem(KEY);
    } catch {
        return null;
    }
}

/** Clears the captured deep link. Call only after a successful navigation. */
export function clearPostLoginRedirect(): void {
    consumed = true;
    inMemory = null;
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        /* noop */
    }
}
